/*!
 * QR Widget Embed 12:40 PM 4/21/2026 DJF
 * ------------------------------------------------------
 * Portable, multi-instance QR widget with:
 * - self-injected HTML and CSS
 * - auto-loading QRCode library
 * - multiple widgets per page
 * - unique IDs per instance
 * - optional auto-created fallback mount
 * - configurable via HTML data attributes or JS options
 *
 * Supported data attributes:
 * - data-qr-title="..."
 * - data-qr-autofill="false"
 * - data-qr-mode="compact|full"
 * - data-qr-theme="light|dark|brand"
 * - data-qr-color="#A4118D"
 * - data-qr-download="true"
 * - data-qr-copy="true"
 * - data-qr-parent="main"
 * - data-qr-position="append|prepend|before|after"
 *
 * Public API:
 * - QrWidget.init(options)
 * - QrWidget.initAll(selector, sharedOptions)
 */

(function () {
  "use strict";

  var QR_LIB_SRC = "https://cdn.jsdelivr.net/npm/qrcode/build/qrcode.min.js";
  var STYLE_ID = "qr-widget-embed-styles";
  var widgetCounter = 0;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      if (window.QRCode) {
        resolve();
        return;
      }

      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        existing.addEventListener("load", resolve, { once: true });
        existing.addEventListener("error", reject, { once: true });
        return;
      }

      var script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        reject(new Error("Failed to load QR library."));
      };
      document.head.appendChild(script);
    });
  }

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .qr-widget {
        --qr-accent: #A4118D;
        --qr-accent-strong: #8d0f78;
        --qr-accent-soft: rgba(164, 17, 141, 0.15);
        --qr-bg: #f3f3f3;
        --qr-card-bg: #ffffff;
        --qr-card-alt-bg: #fafafa;
        --qr-text: #111111;
        --qr-text-muted: #555555;
        --qr-text-soft: #666666;
        --qr-border: rgba(0, 0, 0, 0.08);
        --qr-border-soft: rgba(0, 0, 0, 0.05);
        --qr-input-border: rgba(0, 0, 0, 0.12);
        --qr-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        --qr-button-text: #ffffff;
        width: 100%;
        max-width: 100%;
        padding: 40px 16px;
        box-sizing: border-box;
        background-color: var(--qr-bg);
      }

      .qr-widget[data-qr-theme="dark"] {
        --qr-bg: #111111;
        --qr-card-bg: #1b1b1b;
        --qr-card-alt-bg: #151515;
        --qr-text: #f5f5f5;
        --qr-text-muted: #d0d0d0;
        --qr-text-soft: #aaaaaa;
        --qr-border: rgba(255, 255, 255, 0.10);
        --qr-border-soft: rgba(255, 255, 255, 0.08);
        --qr-input-border: rgba(255, 255, 255, 0.12);
        --qr-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
      }

      .qr-widget[data-qr-theme="brand"] {
        --qr-bg: #f7eef6;
        --qr-card-bg: #ffffff;
        --qr-card-alt-bg: #fcf7fb;
        --qr-text: #1a1a1a;
        --qr-text-muted: #5f4760;
        --qr-text-soft: #7a617a;
        --qr-border: rgba(164, 17, 141, 0.14);
        --qr-border-soft: rgba(164, 17, 141, 0.10);
        --qr-input-border: rgba(164, 17, 141, 0.16);
        --qr-shadow: 0 10px 30px rgba(164, 17, 141, 0.10);
      }

      .qr-widget__outer {
        max-width: 1100px;
        margin: 0 auto;
      }

      .qr-widget__header {
        background: var(--qr-card-bg);
        border-radius: 18px;
        padding: 32px;
        border: 1px solid var(--qr-border);
        box-shadow: var(--qr-shadow);
        margin-bottom: 24px;
      }

      .qr-widget__header-layout {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        gap: 40px;
      }

      .qr-widget__header-content {
        flex: 1;
        text-align: left;
      }

      .qr-widget__header-heading {
        font-size: 32px;
        line-height: 1.2;
        color: var(--qr-text);
        margin: 0 0 12px 0;
        font-weight: 600;
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      .qr-widget__header-description {
        font-size: 16px;
        color: var(--qr-text);
        margin: 0 0 12px 0;
        line-height: 1.5;
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      .qr-widget__header-instructions {
        font-size: 14px;
        color: var(--qr-text-soft);
        margin: 0;
        line-height: 1.4;
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      .qr-widget__store-badges {
        display: flex;
        gap: 12px;
        margin-top: 16px;
        flex-wrap: wrap;
        justify-content: flex-start;
      }

      .qr-widget__badge-link {
        display: inline-block;
        height: 40px;
      }

      .qr-widget__badge-link img {
        height: 100%;
        width: auto;
        display: block;
      }

      .qr-widget__header-qr {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
      }

      .qr-widget__header-qr-box {
        width: 200px;
        height: 200px;
        padding: 16px;
        background-color: var(--qr-card-bg);
        border-radius: 8px;
        border: 1px solid var(--qr-border);
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
      }

      .qr-widget__header-qr-box img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }

      .qr-widget__header-qr-label {
        font-size: 14px;
        color: var(--qr-text);
        font-weight: 500;
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      .qr-widget__body {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 24px;
        align-items: start;
      }

      .qr-widget__form-section,
      .qr-widget__display-section {
        width: 100%;
      }

      .qr-widget__card {
        background: var(--qr-card-bg);
        padding: 32px;
        border-radius: 16px;
        border: 1px solid var(--qr-border);
        box-shadow: var(--qr-shadow);
        box-sizing: border-box;
      }

      .qr-widget__card--display {
        text-align: center;
      }

      .qr-widget__title,
      .qr-widget__display-title {
        margin: 0 0 12px;
        font-family: Inter, "Rethink Sans", sans-serif;
        color: var(--qr-text);
      }

      .qr-widget__subtitle,
      .qr-widget__helper {
        margin: 0 0 20px;
        color: var(--qr-text-muted);
        font-family: Inter, "Rethink Sans", sans-serif;
        line-height: 1.5;
        word-break: break-word;
      }

      .qr-widget__label {
        display: block;
        margin: 0 0 6px;
        font-size: 13px;
        color: var(--qr-text-muted);
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      .qr-widget__input {
        width: 100%;
        padding: 12px 14px;
        margin: 0 0 16px;
        border-radius: 10px;
        border: 1px solid var(--qr-input-border);
        background: var(--qr-card-alt-bg);
        color: var(--qr-text);
        font-size: 14px;
        box-sizing: border-box;
      }

      .qr-widget__input:focus {
        outline: none;
        border-color: var(--qr-accent);
        box-shadow: 0 0 0 2px var(--qr-accent-soft);
        background: var(--qr-card-bg);
      }

      .qr-widget__button {
        width: 100%;
        padding: 14px;
        border: 0;
        border-radius: 12px;
        background: linear-gradient(135deg, var(--qr-accent), var(--qr-accent-strong));
        color: var(--qr-button-text);
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .qr-widget__button:hover {
        transform: translateY(-1px);
      }

      .qr-widget__canvas-wrap {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 282px;
        padding: 16px;
        background: var(--qr-card-alt-bg);
        border-radius: 12px;
        border: 1px solid var(--qr-border-soft);
        margin-bottom: 16px;
      }

      .qr-widget__canvas-wrap canvas {
        max-width: 100%;
        height: auto;
      }

      .qr-widget__action-row {
        display: flex;
        gap: 12px;
        flex-wrap: wrap;
        margin-top: 12px;
      }

      .qr-widget__secondary-button {
        flex: 1 1 160px;
        padding: 12px 14px;
        border-radius: 12px;
        border: 1px solid var(--qr-border);
        background: var(--qr-card-alt-bg);
        color: var(--qr-text);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
      }

      .qr-widget__secondary-button:hover {
        transform: translateY(-1px);
      }

      .qr-widget__status {
        margin-top: 12px;
        font-size: 13px;
        color: var(--qr-text-soft);
        font-family: Inter, "Rethink Sans", sans-serif;
      }

      @media (max-width: 768px) {
        .qr-widget__header-layout {
          flex-direction: column;
          text-align: center;
        }

        .qr-widget__header-content {
          text-align: center;
        }

        .qr-widget__store-badges {
          justify-content: center;
        }

        .qr-widget__header-qr-box {
          width: 160px;
          height: 160px;
        }

        .qr-widget__body {
          grid-template-columns: 1fr;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function detectAppType(website, app) {
    var explicitApp = String(app || "").trim();

    if (explicitApp === "Product") return "Product";
    if (explicitApp === "Store") return "Store";

    var url = String(website || "").toLowerCase();

    if (
      url.indexOf("product-locator") >= 0 ||
      url.indexOf("profind") >= 0 ||
      url.indexOf("/products") >= 0 ||
      url.indexOf("productfinder") >= 0
    ) {
      return "Product";
    }

    if (
      url.indexOf("store-locator") >= 0 ||
      url.indexOf("promap") >= 0 ||
      url.indexOf("/stores") >= 0 ||
      url.indexOf("storefinder") >= 0
    ) {
      return "Store";
    }

    return "Store";
  }

  function getParams() {
    var params = new URLSearchParams(window.location.search);
    var website = params.get("website") || "";
    var rawApp = params.get("app") || "";

    return {
      website: website,
      name: params.get("name") || "",
      custom: params.get("custom") || "",
      app: detectAppType(website, rawApp)
    };
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (char) {
      return ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;"
      })[char];
    });
  }

  function buildQrUrl(website, name, custom, app) {
    var separator = website.indexOf("?") >= 0 ? "&" : "?";
    var normalizedApp = detectAppType(website, app);

    return (
      website +
      separator +
      "name=" + encodeURIComponent(name) +
      "&custom=" + encodeURIComponent(custom) +
      "&app=" + encodeURIComponent(normalizedApp)
    );
  }

  function getInstanceId() {
    widgetCounter += 1;
    return "qrw-" + widgetCounter + "-" + Math.random().toString(36).slice(2, 8);
  }

  function hexToRgb(hex) {
    if (!hex) return null;
    var clean = String(hex).trim().replace("#", "");
    if (clean.length === 3) {
      clean = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
    }
    if (!/^[0-9a-fA-F]{6}$/.test(clean)) return null;

    var num = parseInt(clean, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255
    };
  }

  function rgbToHex(r, g, b) {
    function toHex(n) {
      var clamped = Math.max(0, Math.min(255, Math.round(n)));
      var hex = clamped.toString(16);
      return hex.length === 1 ? "0" + hex : hex;
    }
    return "#" + toHex(r) + toHex(g) + toHex(b);
  }

  function shadeColor(hex, factor) {
    var rgb = hexToRgb(hex);
    if (!rgb) return hex;

    return rgbToHex(
      rgb.r * factor,
      rgb.g * factor,
      rgb.b * factor
    );
  }

  function rgbaFromHex(hex, alpha) {
    var rgb = hexToRgb(hex);
    if (!rgb) return "rgba(164, 17, 141, " + alpha + ")";
    return "rgba(" + rgb.r + ", " + rgb.g + ", " + rgb.b + ", " + alpha + ")";
  }

  function getCurrentScriptTag() {
    return document.currentScript || document.querySelector('script[src*="qr-widget-embed"]');
  }

  function getDataConfig(target) {
    var script = getCurrentScriptTag();

    function getAttr(name) {
      if (target && target.getAttribute(name) !== null) {
        return target.getAttribute(name);
      }
      if (script && script.getAttribute(name) !== null) {
        return script.getAttribute(name);
      }
      return null;
    }

    return {
      title: getAttr("data-qr-title"),
      autofill: getAttr("data-qr-autofill"),
      mode: getAttr("data-qr-mode"),
      theme: getAttr("data-qr-theme"),
      color: getAttr("data-qr-color"),
      download: getAttr("data-qr-download"),
      copy: getAttr("data-qr-copy"),
      parent: getAttr("data-qr-parent"),
      position: getAttr("data-qr-position")
    };
  }

  function normalizeOptions(options, target) {
    var data = getDataConfig(target);
    var theme = (data.theme === "dark" || data.theme === "brand") ? data.theme : "light";
    var accentColor = data.color || "#A4118D";

    var base = {
      target: null,

      headerHeading: data.title || "Scan this QR Code to Setup your Locator",
      autofill: data.autofill === "false" ? false : true,
      mode: data.mode === "compact" ? "compact" : "full",
      theme: theme,
      color: accentColor,
      enableDownload: data.download === "true",
      enableCopy: data.copy === "true",

      headerDescription: "Download our Mobile App to stay connected and find stores in an easy to use app.",
      headerInstructions: "Scan the QR code with your phone camera to download. You can add both our Store Locator and Product Locator to your App.",
      headerQrLabel: "Scan to setup",
      headerQrAlt: "QR code to download app",
      headerQrImage: "https://rpstorelocator.app/cdn/shop/files/rp-white-500x500.jpg?v=1759685744&width=500",

      appStoreLink: "https://rpstorelocator.app/pages/store-locator",
      playStoreLink: "https://rpstorelocator.app/pages/store-locator",
      appStoreBadge: "https://rpstorelocator.app/cdn/shop/files/app-store-badge.png?height=120&v=1774541266",
      playStoreBadge: "https://rpstorelocator.app/cdn/shop/files/play-store-badge.png?height=120&v=1774541266",

      formTitle: "QR Code Generator",
      formSubtitle: "Create a custom QR code for your locator setup.",
      previewTitle: "Generated QR Preview"
    };

    return Object.assign({}, base, options || {});
  }

  function ensureDefaultMount() {
    var existingAuto = document.querySelector("[data-qr-widget]");
    var existingManual = document.querySelector("#my-qr-widget");

    if (existingAuto) return existingAuto;
    if (existingManual) return existingManual;

    var script = getCurrentScriptTag();
    var parentSelector = script ? script.getAttribute("data-qr-parent") : null;
    var position = script ? script.getAttribute("data-qr-position") : null;
    var parent = document.body;

    if (parentSelector) {
      var found = document.querySelector(parentSelector);
      if (found) parent = found;
    }

    var container = document.createElement("div");
    container.id = "my-qr-widget";
    container.style.margin = "40px auto";

    switch (position) {
      case "prepend":
        parent.prepend(container);
        break;
      case "before":
        if (script && script.parentNode) {
          script.parentNode.insertBefore(container, script);
        } else {
          parent.appendChild(container);
        }
        break;
      case "after":
        if (script && script.parentNode) {
          script.parentNode.insertBefore(container, script.nextSibling);
        } else {
          parent.appendChild(container);
        }
        break;
      case "append":
      default:
        parent.appendChild(container);
        break;
    }

    return container;
  }

  function renderWidget(target, options) {
    var instanceId = getInstanceId();
    var accentStrong = shadeColor(options.color, 0.85);
    var accentSoft = rgbaFromHex(options.color, 0.15);

    var ids = {
      website: instanceId + "-website",
      name: instanceId + "-name",
      custom: instanceId + "-custom",
      app: instanceId + "-app",
      button: instanceId + "-generate-btn",
      canvas: instanceId + "-canvas",
      output: instanceId + "-output-text",
      download: instanceId + "-download-btn",
      copy: instanceId + "-copy-btn",
      status: instanceId + "-status"
    };

    target.innerHTML = `
      <div
        class="qr-widget"
        data-qr-widget-instance="${instanceId}"
        data-qr-theme="${escapeHtml(options.theme)}"
        style="--qr-accent: ${escapeHtml(options.color)}; --qr-accent-strong: ${escapeHtml(accentStrong)}; --qr-accent-soft: ${escapeHtml(accentSoft)};"
      >
        <div class="qr-widget__outer">
          ${
            options.mode === "compact"
              ? ""
              : `
            <section class="qr-widget__header">
              <div class="qr-widget__header-layout">
                <div class="qr-widget__header-content">
                  <h2 class="qr-widget__header-heading">${escapeHtml(options.headerHeading)}</h2>
                  <p class="qr-widget__header-description">${escapeHtml(options.headerDescription)}</p>
                  <p class="qr-widget__header-instructions">${escapeHtml(options.headerInstructions)}</p>

                  <div class="qr-widget__store-badges">
                    <a href="${escapeHtml(options.appStoreLink)}" class="qr-widget__badge-link" target="_blank" rel="noopener">
                      <img src="${escapeHtml(options.appStoreBadge)}" alt="Download on the App Store">
                    </a>
                    <a href="${escapeHtml(options.playStoreLink)}" class="qr-widget__badge-link" target="_blank" rel="noopener">
                      <img src="${escapeHtml(options.playStoreBadge)}" alt="Get it on Google Play">
                    </a>
                  </div>
                </div>

                <div class="qr-widget__header-qr">
                  <div class="qr-widget__header-qr-box">
                    <img src="${escapeHtml(options.headerQrImage)}" alt="${escapeHtml(options.headerQrAlt)}">
                  </div>
                  <div class="qr-widget__header-qr-label">${escapeHtml(options.headerQrLabel)}</div>
                </div>
              </div>
            </section>
          `
          }

          <div class="qr-widget__body">
            <section class="qr-widget__form-section">
              <div class="qr-widget__card">
                <h3 class="qr-widget__title">${escapeHtml(options.formTitle)}</h3>
                <p class="qr-widget__subtitle">${escapeHtml(options.formSubtitle)}</p>

                <label class="qr-widget__label" for="${ids.website}">Website</label>
                <input class="qr-widget__input" type="text" id="${ids.website}" placeholder="Website URL">

                <label class="qr-widget__label" for="${ids.name}">Name</label>
                <input class="qr-widget__input" type="text" id="${ids.name}" placeholder="Store Locator Name">

                <label class="qr-widget__label" for="${ids.custom}">Custom Field</label>
                <input class="qr-widget__input" type="text" id="${ids.custom}" placeholder="Custom value">

                <label class="qr-widget__label" for="${ids.app}">App</label>
                <select class="qr-widget__input" id="${ids.app}">
                  <option value="Store" selected>Store</option>
                  <option value="Product">Product</option>
                </select>

                <button class="qr-widget__button" type="button" id="${ids.button}">Generate QR</button>
              </div>
            </section>

            <section class="qr-widget__display-section">
              <div class="qr-widget__card qr-widget__card--display">
                <h3 class="qr-widget__display-title">${escapeHtml(options.previewTitle)}</h3>
                <div class="qr-widget__canvas-wrap">
                  <canvas id="${ids.canvas}" width="250" height="250"></canvas>
                </div>
                <p class="qr-widget__helper" id="${ids.output}">Fill out the form and generate your QR code.</p>

                ${
                  options.enableDownload || options.enableCopy
                    ? `
                  <div class="qr-widget__action-row">
                    ${
                      options.enableDownload
                        ? `<button class="qr-widget__secondary-button" type="button" id="${ids.download}">Download QR</button>`
                        : ""
                    }
                    ${
                      options.enableCopy
                        ? `<button class="qr-widget__secondary-button" type="button" id="${ids.copy}">Copy QR URL</button>`
                        : ""
                    }
                  </div>
                  <div class="qr-widget__status" id="${ids.status}"></div>
                `
                    : ""
                }
              </div>
            </section>
          </div>
        </div>
      </div>
    `;

    var websiteInput = target.querySelector("#" + ids.website);
    var nameInput = target.querySelector("#" + ids.name);
    var customInput = target.querySelector("#" + ids.custom);
    var appInput = target.querySelector("#" + ids.app);
    var generateBtn = target.querySelector("#" + ids.button);
    var canvas = target.querySelector("#" + ids.canvas);
    var outputText = target.querySelector("#" + ids.output);
    var downloadBtn = target.querySelector("#" + ids.download);
    var copyBtn = target.querySelector("#" + ids.copy);
    var statusText = target.querySelector("#" + ids.status);

    var params = getParams();
    var currentQrUrl = "";

    websiteInput.value = params.website;
    nameInput.value = params.name;
    customInput.value = params.custom;
    appInput.value = params.app;

    function setStatus(message) {
      if (statusText) {
        statusText.textContent = message || "";
      }
    }

    function generateQR() {
      var website = websiteInput.value.trim();
      var name = nameInput.value.trim();
      var custom = customInput.value.trim();
      var selectedApp = appInput.value;
      var resolvedApp = detectAppType(website, selectedApp);

      if (!website || !name || !custom || !selectedApp) {
        alert("Please fill in all fields.");
        return;
      }

      appInput.value = resolvedApp;

      var qrUrl = buildQrUrl(website, name, custom, resolvedApp);
      currentQrUrl = qrUrl;
      setStatus("");

      window.QRCode.toCanvas(
        canvas,
        qrUrl,
        { width: 250, margin: 2 },
        function (error) {
          if (error) {
            console.error(error);
            outputText.textContent = "There was a problem generating the QR code.";
            setStatus("Unable to generate QR code.");
            return;
          }

          outputText.textContent = qrUrl;
        }
      );
    }

    generateBtn.addEventListener("click", generateQR);

    if (downloadBtn) {
      downloadBtn.addEventListener("click", function () {
        if (!currentQrUrl) {
          setStatus("Generate a QR code first.");
          return;
        }

        try {
          var dataUrl = canvas.toDataURL("image/png");
          var link = document.createElement("a");
          link.href = dataUrl;
          link.download = "qr-code.png";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setStatus("QR code downloaded.");
        } catch (error) {
          console.error(error);
          setStatus("Unable to download QR code.");
        }
      });
    }

    if (copyBtn) {
      copyBtn.addEventListener("click", function () {
        if (!currentQrUrl) {
          setStatus("Generate a QR code first.");
          return;
        }

        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(currentQrUrl)
            .then(function () {
              setStatus("QR URL copied.");
            })
            .catch(function (error) {
              console.error(error);
              setStatus("Unable to copy QR URL.");
            });
        } else {
          var tempInput = document.createElement("input");
          tempInput.value = currentQrUrl;
          document.body.appendChild(tempInput);
          tempInput.select();

          try {
            document.execCommand("copy");
            setStatus("QR URL copied.");
          } catch (error) {
            console.error(error);
            setStatus("Unable to copy QR URL.");
          }

          document.body.removeChild(tempInput);
        }
      });
    }

    if (
      options.autofill &&
      params.website &&
      params.name &&
      params.custom
    ) {
      generateQR();
    }

    return {
      id: instanceId,
      target: target,
      generateQR: generateQR,
      elements: {
        websiteInput: websiteInput,
        nameInput: nameInput,
        customInput: customInput,
        appInput: appInput,
        button: generateBtn,
        canvas: canvas,
        outputText: outputText,
        downloadBtn: downloadBtn,
        copyBtn: copyBtn,
        statusText: statusText
      }
    };
  }

  function init(options) {
    var rawTarget = options && options.target;
    var target =
      typeof rawTarget === "string"
        ? document.querySelector(rawTarget)
        : rawTarget;

    if (!target) {
      target = ensureDefaultMount();
    }

    if (!target) {
      console.error("QR widget target not found.");
      return Promise.resolve(null);
    }

    var settings = normalizeOptions(options, target);
    settings.target = target;

    injectStyles();

    return loadScript(QR_LIB_SRC)
      .then(function () {
        return renderWidget(target, settings);
      })
      .catch(function (error) {
        console.error(error);
        return null;
      });
  }

  function initAll(selector, sharedOptions) {
    var targets = document.querySelectorAll(selector || "[data-qr-widget]");
    injectStyles();

    return loadScript(QR_LIB_SRC)
      .then(function () {
        return Array.prototype.map.call(targets, function (target) {
          var options = normalizeOptions(
            Object.assign({}, sharedOptions || {}, { target: target }),
            target
          );
          return renderWidget(target, options);
        });
      })
      .catch(function (error) {
        console.error(error);
        return [];
      });
  }

  window.QrWidget = {
    init: init,
    initAll: initAll
  };

  function autoInit() {
    var autoTargets = document.querySelectorAll("[data-qr-widget]");

    if (autoTargets.length > 0) {
      window.QrWidget.initAll("[data-qr-widget]");
    } else {
      var defaultMount = ensureDefaultMount();
      if (defaultMount) {
        window.QrWidget.init({ target: defaultMount });
      }
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit);
  } else {
    autoInit();
  }
})();